import type { CustomerStatement } from '../model/customerStatement.types';

const moneyFormat = new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "18,836.45" */
export const money = (amount: number | null | undefined): string => moneyFormat.format(amount ?? 0);

/** ISO date → dd-mm-yyyy, the way every legacy screen showed dates. */
export const showDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}-${m}-${y}`;
};

/**
 * One customer's statement, laid out as the printed page is: the boxed
 * customer header, the lines with a running balance, the totals, and the
 * 2 × 6 ageing grid. Shared by the statement screen and the Send Statements
 * workspace, so what the operator checks is the same object that is mailed.
 */
export function StatementCard({ statement: s, className = '' }: { statement: CustomerStatement; className?: string }) {
  return (
    <div className={`h-full flex flex-col rounded-xl border border-slate-200/90 bg-white shadow-2xs overflow-hidden ${className}`}>
      {/* Customer block — the boxed header on the printed page */}
      <div className="shrink-0 grid grid-cols-1 gap-2.5 border-b border-slate-200/90 bg-gradient-to-b from-white to-slate-50/60 p-3 sm:p-3.5 md:grid-cols-3">
        <div className="md:col-span-2 min-w-0">
          <div className="text-sm font-black tracking-tight text-slate-900 truncate">{s.customerName}</div>
          <div className="mt-0.5 text-xs text-slate-600 line-clamp-2">
            {[s.address1, s.address2, s.address3].filter(Boolean).join(', ')}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
            {s.phone && <span>Tel: <b className="text-slate-700">{s.phone}</b></span>}
            {s.attn && <span>Attn: <b className="text-slate-800">{s.attn}</b></span>}
          </div>
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-0.5 text-[11px]">
          <dt className="text-slate-500 font-medium">A/C CODE</dt><dd className="font-bold text-slate-900">{s.accountCode ?? '—'}</dd>
          <dt className="text-slate-500 font-medium">TERMS</dt><dd className="text-slate-900">{s.terms ?? '—'}</dd>
          <dt className="text-slate-500 font-medium">CURRENCY</dt><dd className="font-semibold text-slate-900">{s.currency ?? '—'}</dd>
          {s.openingBalance !== 0 && (
            <>
              <dt className="text-slate-500 font-medium">OPENING</dt>
              <dd className="font-bold tabular-nums font-mono text-slate-900" title="Balance brought forward">
                {money(s.openingBalance)}
              </dd>
            </>
          )}
          <dt className="text-slate-500 font-medium">DATE</dt><dd className="text-slate-900">{showDate(s.statementDate)}</dd>
          <dt className="text-slate-500 font-medium">EMAILS</dt><dd className="truncate text-slate-700" title={s.emails.join(', ')}>{s.emails.join(', ') || '—'}</dd>
        </dl>
      </div>

      {/* Lines Table */}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full min-w-[700px] border-collapse text-xs">
          <thead className="sticky top-0 z-10 bg-slate-100 border-b border-slate-200 text-[10px] font-black uppercase tracking-wider text-slate-600 shadow-2xs">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Invoice No</th>
              <th className="px-3 py-2 text-left">Reference</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right">Debit</th>
              <th className="px-3 py-2 text-right">Credit</th>
              <th className="px-3 py-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {s.lines.map((line, i) => (
              <tr
                key={`${line.documentNo}-${i}`}
                className={`transition-colors hover:bg-blue-50/40 ${
                  line.kind === 'CREDIT_NOTE' ? 'bg-rose-50/30' : i % 2 === 1 ? 'bg-slate-50/30' : 'bg-white'
                }`}
              >
                <td className="px-3 py-1.5 tabular-nums text-slate-700 font-mono text-[11px]">{showDate(line.date)}</td>
                <td className="px-3 py-1.5 font-semibold text-blue-700 font-mono text-[11px]">{line.documentNo}</td>
                <td className="px-3 py-1.5 text-slate-600 truncate max-w-[140px]" title={line.reference ?? ''}>{line.reference ?? ''}</td>
                <td className="px-3 py-1.5 text-slate-700 truncate max-w-[200px]" title={line.description ?? ''}>{line.description ?? ''}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-mono font-medium text-slate-900">{money(line.debit)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-mono font-semibold text-emerald-700">{money(line.credit)}</td>
                <td className="px-3 py-1.5 text-right font-semibold tabular-nums font-mono text-slate-900">{money(line.balance)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 z-10 border-t-2 border-slate-300 bg-slate-100 text-xs font-black shadow-2xs">
            <tr>
              <td className="px-3 py-2 text-slate-700" colSpan={4}>TOTAL ({s.currency})</td>
              <td className="px-3 py-2 text-right tabular-nums font-mono">{money(s.totalDebit)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-mono text-emerald-700">{money(s.totalCredit)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-mono text-blue-800">{money(s.closingBalance)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Ageing grid — 2 × 6 in calendar slots */}
      <div className="shrink-0 border-t border-slate-200/90 bg-slate-50/50 p-2.5">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-600">Ageing Breakdown (12 Months)</span>
          <span className="text-[10px] text-slate-500 font-semibold">Balance: <b className="text-blue-700">{s.currency} {money(s.closingBalance)}</b></span>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1 overflow-hidden">
          {[...s.ageing].sort((a, b) => a.month - b.month).map((b) => {
            const hasAmount = Boolean(b.amount && b.amount !== 0);
            return (
              <div
                key={b.label}
                className={`rounded-lg border px-1.5 py-1 text-center transition-all ${
                  hasAmount
                    ? 'border-blue-300 bg-blue-50/90 text-blue-900 shadow-2xs'
                    : 'border-slate-200/70 bg-white text-slate-400'
                }`}
              >
                <div className={`text-[9px] font-bold uppercase ${hasAmount ? 'text-blue-700' : 'text-slate-500'}`}>{b.label}</div>
                <div className={`text-[11px] tabular-nums font-mono ${hasAmount ? 'font-extrabold text-blue-950' : 'text-slate-400'}`}>
                  {money(b.amount)}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-1.5 text-[9px] text-slate-400 text-center tracking-tight">
          WE SHALL BE GRATEFUL IF YOU LET US HAVE PAYMENT AS SOON AS POSSIBLE &bull; ANY DISCREPANCY IN THIS STATEMENT PLEASE INFORM US IN WRITING WITHIN 10 DAYS
        </p>
      </div>
    </div>
  );
}
